// GammaActivationKit.js

import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  AcademicCapIcon,
  SparklesIcon,
  UserPlusIcon,
  PlayCircleIcon,
  EyeIcon,
  BoltIcon,
  RectangleStackIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/solid';

import UrlConfig from '../../../data/UrlConfig';
import { postApi } from '../../../services/apiService';
import { useAuth } from '../../../context/AuthContext';
import { useGlow } from '../../../hooks/useGlow'; // <-- Asegúrate que la ruta sea correcta
import CommentSection from '../../../components/comments/CommentSection';

const giftVideos = [
    {
        title: "The 15-Min Fractal Meditation",
        description: "The complete version of the activation meditation. Perfect for daily practice to enter the Gamma state.",
        url: "https://gusaguiar.com/assets/gamma-activation-kit/Fratal - Gamma - 15m.mp4",
        Icon: PlayCircleIcon,
        iconColor: "text-purple-400"
    },
    {
        title: "The 15-Min Holotrope Gamma Meditation",
        description: "An immersive audio-visual journey with specific guidance from Bashar to deepen your connection.",
        url: "https://gusaguiar.com/assets/gamma-activation-kit/Bashar - Gamma Holotropic Meditation.mp4",
        Icon: SparklesIcon,
        iconColor: "text-sky-400"
    },
    {
        title: "The 15-Min Kaleidoscopic Meditation",
        description: "A powerful visual meditation inspired by Dr. Joe Dispenza's work to create profound internal shifts.",
        url: "https://gusaguiar.com/assets/gamma-activation-kit/Joe Dispenza - Kaleidoscope Meditation - 15m.mp4",
        Icon: EyeIcon,
        iconColor: "text-pink-400"
    }
];


const GammaActivationKit = () => {
    const { user } = useAuth(); // Destructure user from useAuth
    const location = useLocation();
    const [videoErrors, setVideoErrors] = useState({});
    const [validTo, setValidTo] = useState(null);
    const [remainingTime, setRemainingTime] = useState(null);
    const [loadingDuration, setLoadingDuration] = useState(true);
    const [email, setEmail] = useState('');
    const queryParams = new URLSearchParams(location.search);
    const nameParam = queryParams.get('name');
    const titleStyle = useGlow(); // <-- Usamos el custom hook aquí

    const handleVideoError = (videoUrl) => {
        setVideoErrors(prevErrors => ({
            ...prevErrors,
            [videoUrl]: true
        }));
    };

    useEffect(() => {
        // If user is logged in, no need to fetch duration based on email
        if (user) {
            setLoadingDuration(false);
            setValidTo(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)); // Set a far future date for "lifetime"
            setRemainingTime('You have lifetime access!');
            return;
        }

        const emailParam = queryParams.get('email');
        if (emailParam) {
            setEmail(emailParam);

            const fetchDuration = async () => {
                setLoadingDuration(true);
                try {
                    const requestBody = { email: emailParam, product: 'gamma-activation-kit' };
                    console.log("Sending POST request to:", UrlConfig.get_duration, "with body:", requestBody); // For debugging

                    const data = await postApi(UrlConfig.get_duration, requestBody);

                    if (data && data.validTo) {
                        setValidTo(new Date(data.validTo));
                        console.log("ValidTo received:", data.validTo);
                    } else {
                        setValidTo(null);
                        console.log("No validTo data received or is null.");
                    }
                } catch (error) {
                    setValidTo(null);
                } finally {
                    setLoadingDuration(false);
                }
            };
            fetchDuration();
        } else {
            setLoadingDuration(false);
            setValidTo(null);
        }
    }, [location.search, user]); // Added 'user' to dependency array

    // El useEffect para animar el brillo fue removido de aquí y movido al hook useGlow.

    useEffect(() => {
        // If user is logged in, stop the countdown
        if (user) {
            if(remainingTime !== 'You have lifetime access!') setRemainingTime('You have lifetime access!');
            return;
        }

        if (!validTo) {
            setRemainingTime(null);
            return;
        }

        const timer = setInterval(() => {
            const now = new Date();
            const timeLeft = validTo.getTime() - now.getTime();

            if (timeLeft <= 0) {
                setRemainingTime('Your free access has expired.');
                clearInterval(timer);
                return;
            }

            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

            let timeString = [];
            if (days > 0) timeString.push(`${days} day${days > 1 ? 's' : ''}`);
            if (hours > 0) timeString.push(`${hours} hour${hours > 1 ? 's' : ''}`);
            if (minutes > 0) timeString.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
            if (days === 0 && hours === 0 && minutes === 0 && seconds > 0) {
                timeString.push(`${seconds} second${seconds > 1 ? 's' : ''}`);
            }

            if (timeString.length === 0 && timeLeft > 0) {
                 timeString.push('less than a minute');
            }

            setRemainingTime(`You have  ${timeString.join(', ')}  remaining`);
        }, 1000);


        return () => clearInterval(timer);
    }, [validTo, user, remainingTime]);

    // MODIFICATION 1: Create a clear boolean for the expired state.
    const isExpired = validTo && new Date() > validTo;

    // MODIFICATION 2: Use the new isExpired boolean for a more robust check.
    const accessVerifiedAndActive = user || (!loadingDuration && validTo && !isExpired);


    return (
        <div className="relative bg-black text-white min-h-screen font-sans flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>

            <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">

                {/* === SECTION 1: THE GIFTS INTRODUCTION === */}
                <h1
                 style={titleStyle} // <-- Aplicamos el estilo del hook aquí
                 className="text-4xl sm:text-5xl font-bold text-center mb-4 leading-tight">Your Gamma Activation Kit is Here, {nameParam}</h1>
                <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mb-12">Thank you for joining. Below are the three powerful tools we promised. Find a quiet space, use headphones, and enjoy the experience.</p>

                {/* Conditional Video Section */}
                {accessVerifiedAndActive ? (
                    <div className="w-full max-w-4xl space-y-8 mb-20">
                        {giftVideos.map((video) => (
                            <div key={video.title} className="bg-black/30 p-6 rounded-xl border border-gray-800"
                                data-track-click={"gamma-kit-video-play-" + video.title.replace(/\s+/g, '-').toLowerCase()}
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <video.Icon className={`w-8 h-8 flex-shrink-0 ${video.iconColor}`} />
                                    <div>
                                        <h3 className="text-xl font-semibold text-white">{video.title}</h3>
                                        <p className="text-gray-400">{video.description}</p>
                                    </div>
                                </div>

                                {videoErrors[video.url] ? (
                                    <div className="aspect-video w-full rounded-lg border border-red-500/50 bg-red-950/20 flex flex-col items-center justify-center text-center p-4">
                                        <ExclamationTriangleIcon className="w-10 h-10 text-red-400 mb-3" />
                                        <p className="font-semibold text-red-300 mb-2">The video player could not load.</p>
                                        <p className="text-gray-400 text-sm mb-4">This can be due to a network issue or browser setting.</p>
                                        <a
                                            href={video.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block py-2 px-5 rounded-lg bg-red-500 text-white font-bold hover:bg-red-400 transition-all"
                                        >
                                            Open Video in New Tab
                                        </a>
                                    </div>
                                ) : (
                                    <div className="aspect-video w-full rounded-lg overflow-hidden border border-gray-700">
                                        <video
                                            src={video.url}
                                            className="w-full h-full"
                                            controls
                                            loop
                                            controlsList="nodownload"
                                            onError={() => handleVideoError(video.url)}
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    null
                )}

                {/* Access Duration / Validity Message */}
                <div className="w-full max-w-4xl text-center mb-8">
                    {loadingDuration && !user ? (
                        <p className="text-gray-400 text-lg">Loading your access...</p>
                    ) : user ? (
                        <p className="text-green-400 text-2xl font-semibold mb-4 p-3 bg-gray-800/50 rounded-lg border border-green-500/30 shadow-md mb-48">
                            You have lifetime access to these gifts!
                        </p>
                    ) : accessVerifiedAndActive ? (
                        <p className="text-yellow-300 text-2xl font-semibold mb-4 p-3 bg-gray-800/50 rounded-lg border border-yellow-500/30 shadow-md animate-pulse">
                            {remainingTime}.
                        </p>
                    ) : (
                        // MODIFICATION 3: Display the formatted expiration date.
                        <div className="bg-red-950/20 border border-red-500/50 rounded-lg p-4 mb-4 flex flex-col items-center justify-center gap-3">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-400 mb-2" />
                            <p className="text-red-300 text-xl font-semibold">
                                {validTo
                                    ? `Your Kit Expired on ${validTo.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                                    : 'Your free access to these gifts has expired.'
                                }
                            </p>
                        </div>
                    )}

                    {!user && (
                        <div className="mt-6 p-6 bg-black/40 rounded-xl border border-gray-700 shadow-lg">
                            <UserPlusIcon className="w-10 h-10 mx-auto mb-4 text-gray-500" />
                            <h3 className="text-2xl font-semibold mb-3">Want to keep these gifts forever?</h3>
                            <p className="text-gray-400 max-w-2xl mx-auto mb-6">
                                Create a free account to get lifetime access to your Gamma Activation Kit, save your progress, and unlock additional surprises, all in one secure place.
                            </p>
                            <Link
                                to={`/register${email ? `?email=${email}` : ''}`}
                                className="inline-block py-3 px-8 rounded-lg bg-indigo-700/70 border border-indigo-600 text-white font-bold text-lg hover:bg-indigo-600 transition-all shadow-xl transform hover:scale-105"
                                data-track-click="gamma-kit-register-cta"
                            >
                                Create a Free Account Now
                            </Link>
                        </div>
                    )}
                </div>

                {/* === SECTION 2: THE IRRESISTIBLE OFFER (STILL COMMENTED OUT) === */}
                { user &&
                <div className="w-full max-w-6xl text-center bg-gray-900/50 border-2 border-yellow-400 rounded-2xl p-8 shadow-2xl shadow-yellow-400/20 backdrop-blur-sm mb-20">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Go Deeper? A One-Time Offer</h2>
                    <p className="text-lg text-yellow-300/80 max-w-3xl mx-auto mb-10">You can accelerate your journey. Choose one of the foundational programs below for only $7... or get all three for just $15.</p>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                        {/* Option A: The Masterclass */}
                        <div className="bg-black/30 p-6 rounded-xl border border-sky-700/50 flex flex-col text-center"
                            data-track-click="masterclass-offer">
                            <AcademicCapIcon className="w-10 h-10 mx-auto mb-3 text-sky-400" />
                            <h3 className="text-xl font-bold mb-2">The full "Going Gamma" Bashar Transmission</h3>
                            <p className="text-gray-400 flex-grow mb-4">The complete 4-hour deep dive into the mechanics of the Gamma state and conscious creation.</p>
                            <p className="text-3xl font-bold mb-5">$7 <span className="text-base line-through text-gray-500 ml-2">$17</span></p>
                            <Link to="/checkout?product_id=gamma-masterclass&price=7" className="w-full py-3 px-4 rounded-lg bg-gray-800 border border-sky-600 font-semibold hover:bg-sky-600 transition-all">
                                Get for $7
                            </Link>
                        </div>

                        {/* Option B: The Meditation System */}
                        <div className="bg-black/30 p-6 rounded-xl border border-purple-700/50 flex flex-col text-center"
                            data-track-click="fractal-offer">
                            <BoltIcon className="w-10 h-10 mx-auto mb-3 text-purple-400" />
                            <h3 className="text-xl font-bold mb-2">The Complete Fractal System</h3>
                            <p className="text-gray-400 flex-grow mb-4">All four 38-minute fractal meditations (Alpha, Theta, Delta & Gamma) for total state mastery.</p>
                            <p className="text-3xl font-bold mb-5">$7 <span className="text-base line-through text-gray-500 ml-2">$27</span></p>
                            <Link to="/checkout?product_id=fractal-system&price=7" className="w-full py-3 px-4 rounded-lg bg-gray-800 border border-purple-600 font-semibold hover:bg-purple-600 transition-all">
                                Get for $7
                            </Link>
                        </div>

                        {/* Option C: The Kaleidoscope Meditation */}
                        <div className="bg-black/30 p-6 rounded-xl border border-pink-700/50 flex flex-col text-center"
                            data-track-click="kaleidoscope-offer">
                            <EyeIcon className="w-10 h-10 mx-auto mb-3 text-pink-400" />
                            <h3 className="text-xl font-bold mb-2">The Kaleidoscope Meditation</h3>
                            <p className="text-gray-400 flex-grow mb-4">The full 1-hour immersive visual journey inspired by Dr. Joe Dispenza's work.</p>
                            <p className="text-3xl font-bold mb-5">$7 <span className="text-base line-through text-gray-500 ml-2">$37</span></p>
                            <Link to="/checkout?product_id=kaleidoscope-meditation&price=7" className="w-full py-3 px-4 rounded-lg bg-gray-800 border border-pink-600 font-semibold hover:bg-pink-600 transition-all">
                                Get for $7
                            </Link>
                        </div>
                    </div>

                    {/* THE BUNDLE OFFER */}
                    <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 p-1 rounded-2xl"
                        data-track-click="gamma-bundle-offer">
                        <div className="bg-black/80 p-8 rounded-xl">
                            <RectangleStackIcon className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
                            <h3 className="text-3xl font-bold text-white mb-3">Get The Entire Bundle & Save</h3>
                            <p className="text-gray-300 max-w-2xl mx-auto mb-6">Get all three programs above—The Masterclass, The Fractal System, AND The Kaleidoscope Meditation—for one single payment.</p>
                            <p className="text-5xl font-bold text-white mb-6">$15 <span className="text-2xl line-through text-gray-500 ml-3">$81</span></p>
                            <Link to="/checkout?product_id=irresistible-bundle&price=15" className="inline-block py-4 px-10 rounded-lg bg-yellow-500 text-black font-bold text-xl hover:bg-yellow-400 transition-all transform hover:scale-105">
                                Yes! Give Me Everything for $15
                            </Link>
                        </div>
                    </div>
                </div>
                }
            <CommentSection threadId={'gamma-activation-kit-111'} />
            </main>

            <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
                © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
            </footer>
        </div>
    );
};

export default GammaActivationKit;